<template>
  <div ref="componentRef" class="component">
    <div :id="ssrData?.wrapperId || `fallback-wrap-${instanceId}`" class="ster-ad-wrapper">
      <span class="ster-header">
        Advertentie via
        <a target="_blank" href="https://www.ster.nl/online-reclame-bij-ster/">Ster.nl</a>
      </span>
      <div
        class="ad-container"
        :style="{
          width: computedSize.width + 'px',
          minHeight: computedSize.height + 'px',
          background: 'hsl(var(--color-primary-100))',
        }"
      >
        <div
          :id="ssrData?.slotId || `fallback-slot-${instanceId}`"
          :key="computedSlotId"
          class="ster-slot-target"
          :style="{
            width: computedSize.width + 'px',
            height: computedSize.height + 'px',
            visibility: isScriptLoaded ? 'visible' : 'hidden',
          }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, nextTick, onMounted } from 'vue';

const broadcaster = 'avrotros';
const projectName = 'eenvandaag';
const bodyPadding = 16;

const size = {
  large: { width: 728, height: 90, sterKey: 'homepage' },
  small: { width: 300, height: 100, sterKey: 'algemeen' },
};

// 1. GENERATE IDENTIFIERS
// This instanceId might drift, so we don't use it in the useAsyncData KEY.
const instanceId = useId();

// We create a fingerprint based on the ad config.
// This key will be identical on server and client.
const adFingerprint = computed(() => `ster-${broadcaster}-${projectName}`);

// 2. ID TRANSPORT
// We use the stable fingerprint as the key.
const { data: ssrData } = await useAsyncData(
  `transport-${adFingerprint.value}`,
  () => {
    // We capture the server's version of the IDs
    return {
      wrapperId: String(`ster-wrapper-${instanceId}`),
      slotId: String(`ster-slot-${instanceId}`),
    };
  },
  { server: true, immediate: true },
);

const isScriptLoaded = ref(false);
const showLarge = ref(false);
const mediaQuery = ref<MediaQueryList | null>(null);

const computedSlotId = computed(
  () => `_${broadcaster}_${projectName}_${size[showLarge.value ? 'large' : 'small'].sterKey}_`,
);
const computedSize = computed(() => size[showLarge.value ? 'large' : 'small']);

// 3. AD REQUEST LOGIC
const requestAd = () => {
  // Use the slotId from ssrData to ensure we find the element rendered by the server
  const targetId = ssrData.value?.slotId || `fallback-slot-${instanceId}`;

  if (import.meta.server || !window.ootag || !targetId) return;

  window.ootag.queue = window.ootag.queue || [];
  window.ootag.queue.push(() => {
    isScriptLoaded.value = false;

    if (!window.ootag.isInitialized) {
      window.ootag.initializeOo({
        publisher: 1,
        noLogging: 1,
        alwaysNoConsent: 1,
        noRequestsOnPageLoad: 1,
      });
      window.ootag.isInitialized = true;
    }

    window.ootag.defineSlot({
      adSlot: computedSlotId.value,
      targetId: String(targetId),
      emptyCallback: () => {
        isScriptLoaded.value = false;
        handleLog(computedSlotId.value, false);
      },
      filledCallback: () => {
        nextTick(() => {
          isScriptLoaded.value = true;
        });
      },
    });
    window.ootag.makeRequests(true);
  });
};

// 4. LIFECYCLE & EVENT HANDLERS
const handleMediaQueryChange = (event: MediaQueryListEvent | MediaQueryList) => {
  showLarge.value = event.matches;
};

const componentRef = ref<HTMLElement | null>(null);

const initLoading = () => {
  if (typeof window.ootag === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdn.optoutadvertising.com/script/ootag.v2.min.js';
    script.async = true;
    script.onload = () => requestAd();
    document.head.appendChild(script);
  } else {
    requestAd();
  }
};

onMounted(() => {
  mediaQuery.value = window.matchMedia(`(min-width: ${size.large.width + bodyPadding * 2}px)`);
  showLarge.value = mediaQuery.value.matches;
  mediaQuery.value.addEventListener('change', handleMediaQueryChange);

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        initLoading();
        observer.disconnect();
      }
    },
    { rootMargin: '200px' },
  );

  if (componentRef.value) {
    observer.observe(componentRef.value);
  }
});

watch(showLarge, () => {
  if (window.ootag?.undefineAllSlots) {
    window.ootag.undefineAllSlots();
  }
  requestAd();
});

onBeforeUnmount(() => {
  mediaQuery.value?.removeEventListener('change', handleMediaQueryChange);
  if (window.ootag?.undefineAllSlots) {
    window.ootag.undefineAllSlots();
  }
});

const handleLog = (slot: string, filled: boolean) => {
  if (!import.meta.client) return;
  try {
    const url = encodeURIComponent(window.location.href.toLowerCase().split('?')[0]);
    const endpoint = `https://sterdisplayhub.azurewebsites.net/lg?sl=${slot}&url=${url}&filled=${filled}`;
    navigator.sendBeacon(endpoint);
  } catch (e) {
    console.warn('Ster log failed', e);
  }
};
</script>

<style scoped>
.component {
  display: flex;
  justify-content: center;
}
.ad-container {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  overflow: hidden;
}
.ster-slot-target {
  transition: opacity 0.3s ease;
}
.ster-header {
  display: block;
  text-align: center;
  color: hsl(var(--color-primary-500));
  padding-bottom: 8px;
  font-size: 14px;
  a {
    text-decoration: underline;
    &:hover {
      color: hsl(var(--color-primary-600));
    }
  }
}
.ster-ad-wrapper {
  width: fit-content;
}
</style>
